import { SMARTY_INTL_STREET_KEY } from '../utils/smartyConfig';
import { normalizePostalCodeValue } from '../utils/validators';
import useAddressValidationBase from './useAddressValidationBase';

const CACHE_NAMESPACE = 'smarty-intl-validation';
const CACHE_TTL_MS = 60 * 1000;
const RATE_LIMIT_NAMESPACE = 'smarty-intl-validation';
const RATE_LIMIT_CONFIG = {
  limit: 5,
  windowMs: 60 * 1000,
  cooldownMs: 60 * 1000
};
const VERIFY_URL = 'https://international-street.api.smarty.com/verify';

const PRECISION_LABELS = {
  DeliveryPoint: 'delivery point',
  Premise: 'premise',
  Thoroughfare: 'street',
  Locality: 'city',
  AdministrativeArea: 'region'
};

const getLookupAddressLine = (formData = {}, lookupField = 'address1') => (
  formData?.[lookupField] || ''
);

const buildValidationParams = (formData, countryCode, lookupField = 'address1') => {
  const params = {
    key: SMARTY_INTL_STREET_KEY,
    country: countryCode,
    address1: getLookupAddressLine(formData, lookupField),
    locality: formData.city || '',
    administrative_area: formData.region || '',
    postal_code: normalizePostalCodeValue(formData.postalCode || ''),
    geocode: true
  };

  Object.keys(params).forEach((key) => {
    if (!params[key]) {
      delete params[key];
    }
  });

  return params;
};

const buildRequestKey = (formData, countryCode, lookupField = 'address1') => JSON.stringify({
  countryCode,
  address1: getLookupAddressLine(formData, lookupField),
  locality: formData.city || '',
  administrativeArea: formData.region || '',
  postalCode: normalizePostalCodeValue(formData.postalCode || '')
});

const getPrecisionLabel = (precision) => PRECISION_LABELS[precision] || 'available level';

const buildValidationSummary = (item) => {
  const verificationStatus = item.analysis?.verification_status || 'None';
  const precision = item.analysis?.address_precision || 'None';
  const subBuildingChange = item.changes?.sub_building || item.changes?.components?.sub_building;
  const hasVerifiedSubBuilding = subBuildingChange?.startsWith('Verified');

  if (verificationStatus === 'Verified') {
    if (item.address2 && subBuildingChange && hasVerifiedSubBuilding) {
      return {
        code: 'verified-sub-building-changed',
        title: 'Verified with unit changes',
        message: 'This address is verified, but the apartment or suite information was changed.',
        sortPriority: 2
      };
    }

    return {
      code: 'verified',
      title: 'Verified',
      message: `This address is verified to the ${getPrecisionLabel(precision)} level.`,
      sortPriority: 1
    };
  }

  if (verificationStatus === 'Partial') {
    return {
      code: 'partial',
      title: 'Partially verified',
      message: `This address could only be verified to the ${getPrecisionLabel(precision)} level.`,
      sortPriority: 3
    };
  }

  if (verificationStatus === 'Ambiguous') {
    return {
      code: 'ambiguous',
      title: 'Multiple matches found',
      message: `Multiple possible matches were found at the ${getPrecisionLabel(precision)} level.`,
      sortPriority: 4
    };
  }

  return {
    code: 'not-verified',
    title: 'Not verified',
    message: 'This address could not be verified.',
    sortPriority: 5
  };
};

const transformSuggestions = (responseData = []) => responseData
  .map((item) => ({
    ...item,
    displayText: [
      item.address1,
      item.address2,
      item.address3
    ].filter(Boolean).join(', '),
    validationSummary: buildValidationSummary(item)
  }))
  .sort((left, right) => left.validationSummary.sortPriority - right.validationSummary.sortPriority);

const useIntlAddressValidation = (formData, countryCode, trigger, lookupField = 'address1') => {
  const lookupValue = getLookupAddressLine(formData, lookupField);
  const isEnabled = Boolean(trigger && trigger !== 0 && lookupValue && countryCode);
  const requestParams = buildValidationParams(formData, countryCode, lookupField);
  const requestKey = `smarty-intl-validation:${buildRequestKey(formData, countryCode, lookupField)}`;

  return useAddressValidationBase({
    isEnabled,
    cacheNamespace: CACHE_NAMESPACE,
    cacheTtlMs: CACHE_TTL_MS,
    rateLimitNamespace: RATE_LIMIT_NAMESPACE,
    rateLimitConfig: RATE_LIMIT_CONFIG,
    requestKey,
    requestUrl: VERIFY_URL,
    requestParams,
    transformResponse: transformSuggestions,
    effectDeps: [trigger, lookupField]
  });
};

export default useIntlAddressValidation;
